import {
  WELCOME_GALAXY_INCLINATION,
  WELCOME_GALAXY_POSITION_ANGLE,
  welcomeGalaxyPlacement,
  type WelcomeGalaxyViewport,
} from "#welcome-galaxy-timeline";

/**
 * Dragging the welcome galaxy works like a turntable: the disk turns so the point under the finger
 * follows it around the core. Screen points are mapped back onto the tilted disk plane (undoing
 * the on-screen rotation and the inclination's squash), so a drag along the flattened ellipse turns
 * the disk by the angle it actually sweeps.
 */

/** Closer to the core than this (disk radii), the finger's angle is too jumpy to steer by. */
export const WELCOME_GALAXY_SPIN_DEAD_RADIUS = 0.12;
/** Caps a fling so a hard swipe spins the galaxy briskly rather than into a blur (rad/s). */
export const WELCOME_GALAXY_MAX_FLING_SPEED = 9;

const COS_POSITION_ANGLE = Math.cos(WELCOME_GALAXY_POSITION_ANGLE);
const SIN_POSITION_ANGLE = Math.sin(WELCOME_GALAXY_POSITION_ANGLE);
const COS_INCLINATION = Math.cos(WELCOME_GALAXY_INCLINATION);

export type WelcomeGalaxyDiskPoint = { angle: number; radius: number; x: number; y: number };

/** The disk-plane point (in disk radii, before the spin) under the screen point `(x, y)`. */
export function welcomeGalaxyDiskPointAt(
  x: number,
  y: number,
  viewport: WelcomeGalaxyViewport,
): WelcomeGalaxyDiskPoint {
  "worklet";
  const placement = welcomeGalaxyPlacement(viewport);
  const scale = Math.max(placement.radius, 1);
  const dx = (x - placement.centerX) / scale;
  const dy = (y - placement.centerY) / scale;
  const diskX = dx * COS_POSITION_ANGLE + dy * SIN_POSITION_ANGLE;
  const diskY = (-dx * SIN_POSITION_ANGLE + dy * COS_POSITION_ANGLE) / COS_INCLINATION;
  return {
    angle: Math.atan2(diskY, diskX),
    radius: Math.hypot(diskX, diskY),
    x: diskX,
    y: diskY,
  };
}

/** `angle` wrapped into (-π, π], so a drag across the ±π seam doesn't spin a full turn. */
export function wrapWelcomeGalaxyAngle(angle: number): number {
  "worklet";
  const turn = 2 * Math.PI;
  const wrapped = angle - turn * Math.floor((angle + Math.PI) / turn);
  return wrapped === -Math.PI ? Math.PI : wrapped;
}

/**
 * The spin rate (rad/s) a finger at screen point `(x, y)` moving at `(velocityX, velocityY)` px/s
 * imparts: the disk-plane velocity's tangential component over the radius. Zero inside the core's
 * dead zone, and capped at `WELCOME_GALAXY_MAX_FLING_SPEED`.
 */
export function welcomeGalaxyFlingSpeed(
  x: number,
  y: number,
  velocityX: number,
  velocityY: number,
  viewport: WelcomeGalaxyViewport,
): number {
  "worklet";
  const point = welcomeGalaxyDiskPointAt(x, y, viewport);
  if (point.radius < WELCOME_GALAXY_SPIN_DEAD_RADIUS) {
    return 0;
  }
  const scale = Math.max(welcomeGalaxyPlacement(viewport).radius, 1);
  const vx = velocityX / scale;
  const vy = velocityY / scale;
  const diskVelocityX = vx * COS_POSITION_ANGLE + vy * SIN_POSITION_ANGLE;
  const diskVelocityY = (-vx * SIN_POSITION_ANGLE + vy * COS_POSITION_ANGLE) / COS_INCLINATION;
  const speed = (point.x * diskVelocityY - point.y * diskVelocityX) / (point.radius * point.radius);
  return Math.max(-WELCOME_GALAXY_MAX_FLING_SPEED, Math.min(WELCOME_GALAXY_MAX_FLING_SPEED, speed));
}
